import { createSendConfirmations } from "./send-confirmations";
const separator = "\u001e";
const eventNames = {
  ReceiveChats: "GetAll",
  ReceiveMessages: "GetMessage",
  ReceiveMessage: "SendMessage",
  ChatReady: "CreateChat",
};

/** Converts the retired hub path to the current authenticated chat endpoint. */
export function panelChatUrl(address, token) {
  const url = new URL(address);
  if (/^\/chathub\/?$/i.test(url.pathname)) url.pathname = "/chat";
  url.searchParams.set("access_token", token);
  return url.toString();
}

/** Adapts backend JSON casing at the panel's realtime boundary. */
export function panelChatPayload(value) {
  if (Array.isArray(value)) return value.map(panelChatPayload);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [
    key.charAt(0).toLowerCase() + key.slice(1), panelChatPayload(child),
  ]));
}

/** Runs the JSON SignalR handshake, heartbeat and panel event adapter. */
export function openPanelChat(address, token, { onFrame, onError, onReady, onRequestError }) {
  const ws = new WebSocket(panelChatUrl(address, token));
  const pending = new Map();
  const refreshingChats = new Set();
  const confirmations = createSendConfirmations();
  let invocation = 0;
  let disposed = false;
  let heartbeat;
  let buffer = "";
  let requestedChats = false;
  const fail = () => { confirmations.rejectAll(); if (!disposed) onError("Chat connection failed. Please retry."); };
  const timeout = setTimeout(() => {
    fail();
    ws.close();
  }, 15000);

  function invoke(target, args = []) {
    if (ws.readyState !== WebSocket.OPEN) { fail(); return; }
    const invocationId = String(++invocation);
    pending.set(invocationId, { target, args });
    ws.send(JSON.stringify({ type: 1, invocationId, target, arguments: args }) + separator);
  }

  function requestChats() {
    if (requestedChats) return;
    requestedChats = true;
    invoke("GetChats");
  }

  ws.onopen = () => ws.send(JSON.stringify({ protocol: "json", version: 1 }) + separator);
  ws.onerror = fail;
  ws.onclose = () => { clearInterval(heartbeat); clearTimeout(timeout); fail(); };
  ws.onmessage = ({ data }) => {
    if (disposed) return;
    buffer += data;
    const frames = buffer.split(separator);
    buffer = frames.pop();
    for (const raw of frames) {
      if (!raw) continue;
      let frame;
      try { frame = JSON.parse(raw); } catch { fail(); continue; }
      if (frame.error) { fail(); continue; }
      if (frame.type === undefined) {
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 6 }) + separator);
        }, 15000);
        requestChats();
      } else if (frame.type === 7) {
        fail();
        ws.close();
      } else if (frame.type === 3) {
        const request = pending.get(frame.invocationId);
        pending.delete(frame.invocationId);
        if (request?.target === "SendMessage" && !request.failed) {
          refreshingChats.add(Number(request.args[0].chatId));
          invoke("GetMessages", [{ chatId: request.args[0].chatId, page: 1, limit: 100 }]);
          invoke("GetChats");
        }
      } else if (frame.type === 1) {
        if (frame.target === "Connected") { onReady(); requestChats(); continue; }
        if (frame.target === "ChatCreated") { invoke("GetChats"); continue; }
        if (["ChatDisabled", "ChatError", "MessageError"].includes(frame.target)) {
          confirmations.rejectAll();
          for (const request of pending.values()) if (request.target === "SendMessage") request.failed = true;
          const report = frame.target === "MessageError" && onRequestError ? onRequestError : onError;
          report(panelChatPayload(frame.arguments?.[0])?.message || "Chat request failed.");
          continue;
        }
        const target = eventNames[frame.target];
        if (!target) continue;
        const received = panelChatPayload(frame.arguments?.[0]);
        if (target === "GetMessage" && refreshingChats.has(Number(received?.chatId)) && Number(received?.page || 1) === 1) {
          received.refresh = true;
          refreshingChats.delete(Number(received.chatId));
        }
        if (received?.code === 200) {
          if (target === "GetMessage") confirmations.accept(received.data);
          if (target === "SendMessage") confirmations.accept([received.data]);
        }
        const payload = target === "CreateChat" && received?.id > 0
          ? { code: 200, data: received } : received;
        if (target === "GetAll") clearTimeout(timeout);
        onFrame({ type: 1, target, arguments: [null, payload] });
        if (target === "CreateChat") invoke("GetChats");
      }
    }
  };

  return {
    sendConfirmed(request) {
      return confirmations.wait(request, () => {
        if (disposed || ws.readyState !== WebSocket.OPEN) throw new Error("Chat is disconnected.");
        invoke("SendMessage", [{ chatId: request.id, message: request.text }]);
      });
    },
    send(raw) {
      const request = JSON.parse(raw.endsWith(separator) ? raw.slice(0, -1) : raw);
      if (request.target === "Connect") return;
      const target = { GetAll: "GetChats", GetMessage: "GetMessages" }[request.target] || request.target;
      invoke(target, request.arguments);
    },
    close() {
      disposed = true;
      clearTimeout(timeout);
      clearInterval(heartbeat);
      pending.clear();
      refreshingChats.clear();
      confirmations.rejectAll();
      ws.close();
    },
  };
}
